import React, { useEffect, useState } from 'react'
import { MultiValue, SingleValue } from 'react-select'
import { CompanyListItem } from '../../charts/core/_models'
import moment from 'moment'
import BenchmarkFilter from './BenchmarkFilter'
import { benchmarkHeatmapData } from '../core/_requests'
import { BenchmartHeatmapData } from '../core/_models'
import ApexCharts from 'react-apexcharts'

function BenchmarkHeatmap() {
  let [companyFilter, setCompanyFilter] = useState<SingleValue<CompanyListItem>>(null)
  let [siteFilter, setSiteFilter] = useState<MultiValue<CompanyListItem>>([])
  let [period, setPeriod] = useState<string>('this_year')
  let [startDate, setStartDate] = useState<moment.Moment>(moment())
  let [endDate, setEndDate] = useState<moment.Moment>(moment())
  let [isLoading, setIsLoading] = useState<boolean>(false)
  let [chartData, setChartData] = useState<BenchmartHeatmapData>()
  let [selectedFeature, setSelectedFeature] = useState<string>('')
  let [selectedFeatureName, setSelectedFeatureName] = useState<{
    name: string
    unit: string
  }>({
    name: '',
    unit: '',
  })
  let [meterType, setMeterType] = useState<string>('combine')
  let [chartType, setChartType] = useState<'combination'>('combination')

  const getDateRangeLabel = () => {
    if (period === 'custom') {
      return `${startDate.format('DD MMMM YYYY')} - ${endDate.format('DD MMMM YYYY')}`
    }
    if (period === 'this_year') {
      return `${moment().startOf('year').format('DD MMMM YYYY')} - ${moment().format(
        'DD MMMM YYYY'
      )}`
    }
    if (period === 'last_one_month') {
      return `${moment().subtract(1, 'month').format('DD MMMM YYYY')} - ${moment().format(
        'DD MMMM YYYY'
      )}`
    }
    if (period === 'last_one_week') {
      return `${moment().subtract(1, 'week').format('DD MMMM YYYY')} - ${moment().format(
        'DD MMMM YYYY'
      )}`
    }
    return ''
  }

  const fetchChartData = async () => {
    try {
      setIsLoading(true)
      const params = {
        isLoggedIn: true,
        companyId: Number(companyFilter?.value),
        siteId: siteFilter,
        period: period,
        customStartDate: startDate.format('YYYY-MM-DD'),
        customEndDate: endDate.format('YYYY-MM-DD'),
        siteFeature: selectedFeature,
        meterType: meterType,
      }
      const data = await benchmarkHeatmapData(params)
      setChartData(data)
    } catch (error) {
      console.error('Error fetching chart data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (companyFilter && selectedFeature) {
      fetchChartData()
    }
  }, [companyFilter, siteFilter, selectedFeature, meterType])

  useEffect(() => {
    if (companyFilter && selectedFeature && period === 'custom' && startDate && endDate) {
      fetchChartData()
    } else if (companyFilter && period !== 'custom') {
      fetchChartData()
    }
  }, [startDate, endDate, period])

  useEffect(() => {
    setSiteFilter([])
  }, [companyFilter])

  // Format number with commas and units
  const formatValue = (value: number, unit?: string) => {
    const formattedNumber = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value)
    return unit ? `${formattedNumber} ${unit}` : formattedNumber
  }

  // Prepare data for chart
  const prepareChartData = () => {
    if (!chartData) return null

    const sites = chartData.electricity_consumption.data.map((item) => item.x)
    const electricityValues = chartData.electricity_consumption.data.map((item) => item.y)
    const gasValues = chartData.gas_consumption.data.map((item) => item.y)
    const totalValues = chartData.total_combined_consumption.data.map((item) => item.y)
    const perFeatureValues = chartData.consumption_per_feature.data.map((item) => item.y)

    return {
      sites,
      electricity: electricityValues,
      gas: gasValues,
      total: totalValues,
      perFeature: perFeatureValues,
    }
  }

  const data = prepareChartData()

  // Combination Chart Options - Fixed total consumption issue
  const getCombinationOptions = () => ({
    chart: {
      type: 'line' as const,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      background: 'transparent',
      fontFamily: 'Inter, sans-serif',
    },
    stroke: {
      width: [0, 0, 3],
      curve: 'smooth' as const,
    },
    plotOptions: {
      bar: {
        columnWidth: '50%',
        endingShape: 'rounded' as const,
      },
    },
    fill: {
      opacity: [0.85, 0.85, 1],
      gradient: {
        inverseColors: false,
        shade: 'light',
        type: 'vertical',
        opacityFrom: 0.85,
        opacityTo: 0.55,
        stops: [0, 100, 100],
      },
    },
    labels: data?.sites || [],
    markers: {
      size: [0, 0, 6],
      colors: ['#fff'],
      strokeColors: ['#dc3545'],
      strokeWidth: 2,
      hover: {
        size: 8,
      },
    },
    xaxis: {
      type: 'category' as const,
      labels: {
        style: {
          colors: '#64748B',
          fontSize: '12px',
          fontWeight: 500,
        },
        rotate: -45,
        maxHeight: 120,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: [
      {
        title: {
          text: 'Energy Consumption (kWh)',
          style: {
            color: '#64748B',
            fontSize: '12px',
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: '#64748B',
          },
          formatter: function (val: number) {
            return formatValue(val)
          },
        },
      },
      {
        opposite: true,
        title: {
          text: `Per ${selectedFeatureName.name || 'Feature'} (${selectedFeatureName.unit || ''})`,
          style: {
            color: '#dc3545',
            fontSize: '12px',
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: '#dc3545',
          },
          formatter: function (val: number) {
            return formatValue(val, selectedFeatureName.unit)
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val: number, opts: any) {
          const seriesName = opts.w.config.series[opts.seriesIndex].name
          if (seriesName.includes('Per')) {
            return formatValue(val, selectedFeatureName.unit)
          }
          return formatValue(val, 'kWh')
        },
      },
    },
    legend: {
      position: 'top' as 'top',
      horizontalAlign: 'left' as 'left',
      fontSize: '12px',
      fontWeight: 500,
      labels: {
        colors: '#64748B',
      },
      markers: {
        width: 12,
        height: 12,
        radius: 3,
      },
    },
    colors: ['#007bff', '#28a745', '#dc3545'],
    grid: {
      borderColor: '#e2e8f0',
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          legend: {
            position: 'bottom' as 'bottom',
          },
          xaxis: {
            labels: {
              rotate: -90,
            },
          },
        },
      },
    ],
  })

  // Heatmap Options - Optimized for performance
  const getHeatmapOptions = (
    title: string,
    dataKey: 'electricity' | 'gas' | 'total' | 'perFeature'
  ) => ({
    chart: {
      type: 'heatmap' as const,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      background: 'transparent',
      fontFamily: 'Inter, sans-serif',
      animations: {
        enabled: false, // Disable animations for better performance
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: ['#fff'],
        fontSize: '10px',
        fontWeight: 600,
      },
      formatter: function (val: number) {
        return formatValue(val, dataKey === 'perFeature' ? selectedFeatureName.unit : 'kWh')
      },
    },
    colors: [
      dataKey === 'electricity'
        ? '#007bff'
        : dataKey === 'gas'
        ? '#28a745'
        : dataKey === 'total'
        ? '#ffc107'
        : '#17a2b8',
    ],
    title: {
      text: title,
      style: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#374151',
      },
    },
    xaxis: {
      type: 'category' as const,
      categories: ['Value'],
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#64748B',
          fontSize: '10px',
          fontWeight: 500,
        },
      },
    },
    grid: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return formatValue(val, dataKey === 'perFeature' ? selectedFeatureName.unit : 'kWh')
        },
      },
    },
  })

  const getCombinationSeries = () => {
    if (!data) return []
    return [
      {
        name: 'Electricity',
        type: 'column',
        data: data.electricity,
      },
      {
        name: 'Gas',
        type: 'column',
        data: data.gas,
      },
      {
        name: `Per ${selectedFeatureName.name || 'Feature'}`,
        type: 'line',
        yAxisIndex: 1,
        data: data.perFeature,
      },
    ]
  }

  // Radar Chart Options - Optimized for performance
  const getRadarOptions = (color: string) => ({
    chart: {
      type: 'radar' as const,
      toolbar: {
        show: false,
      },
      background: 'transparent',
      offsetY: 10,
      animations: {
        enabled: false, // Disable animations for better performance
      },
    },
    xaxis: {
      categories: data?.sites || [],
      labels: {
        show: true,
        style: {
          colors: '#64748B',
          fontSize: '9px',
          fontWeight: 500,
        },
        offsetY: 5,
      },
    },
    yaxis: {
      show: false,
    },
    plotOptions: {
      radar: {
        size: 100,
        offsetX: 0,
        offsetY: 0,
        polygons: {
          strokeColors: '#e2e8f0',
          strokeWidth: '1',
          fill: {
            colors: ['#f8fafc', '#f1f5f9'],
          },
        },
      },
    },
    colors: [color, '#ff6b6b'],
    markers: {
      size: [4, 0],
      colors: ['#fff', '#ff6b6b'],
      strokeColor: [color, '#ff6b6b'],
      strokeWidth: [2, 2],
    },
    fill: {
      opacity: [0.15, 0.05],
    },
    stroke: {
      width: [2, 2],
      dashArray: [0, 5],
    },
    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          const seriesName = opts.w.config.series[opts.seriesIndex].name
          if (seriesName.includes('Average')) {
            return `Average: ${formatValue(
              val,
              seriesName.includes('Per') ? selectedFeatureName.unit : 'kWh'
            )}`
          }
          if (seriesName.includes('Per')) {
            return formatValue(val, selectedFeatureName.unit)
          }
          return formatValue(val, 'kWh')
        },
      },
    },
    legend: {
      show: true,
      position: 'bottom' as const,
      fontSize: '8px',
      labels: {
        colors: '#64748B',
      },
      markers: {
        width: 6,
        height: 6,
      },
      itemMargin: {
        horizontal: 6,
        vertical: 2,
      },
    },
  })

  const getElectricityRadarSeries = () => {
    if (!data) return []
    const average = data.electricity.reduce((a, b) => a + b, 0) / data.electricity.length
    const averageArray = new Array(data.sites.length).fill(average)

    return [
      {
        name: 'Electricity Consumption',
        data: data.electricity,
      },
      {
        name: 'Average Line',
        data: averageArray,
      },
    ]
  }

  const getGasRadarSeries = () => {
    if (!data) return []
    const average = data.gas.reduce((a, b) => a + b, 0) / data.gas.length
    const averageArray = new Array(data.sites.length).fill(average)

    return [
      {
        name: 'Gas Consumption',
        data: data.gas,
      },
      {
        name: 'Average Line',
        data: averageArray,
      },
    ]
  }

  const getTotalRadarSeries = () => {
    if (!data) return []
    const average = data.total.reduce((a, b) => a + b, 0) / data.total.length
    const averageArray = new Array(data.sites.length).fill(average)

    return [
      {
        name: 'Total Consumption',
        data: data.total,
      },
      {
        name: 'Average Line',
        data: averageArray,
      },
    ]
  }

  const getPerFeatureRadarSeries = () => {
    if (!data) return []
    const average = data.perFeature.reduce((a, b) => a + b, 0) / data.perFeature.length
    const averageArray = new Array(data.sites.length).fill(average)

    return [
      {
        name: `Per ${selectedFeatureName.name || 'Feature'}`,
        data: data.perFeature,
      },
      {
        name: 'Average Line',
        data: averageArray,
      },
    ]
  }

  return (
    <>
      {/* Date Range Header */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card border-0 shadow-sm'>
            <div className='card-body py-3'>
              <div className='d-flex align-items-center'>
                <div className='d-flex align-items-center'>
                  <div className='bg-primary bg-opacity-10 rounded-circle p-2 me-3'>
                    <i className='fas fa-calendar-alt text-primary'></i>
                  </div>
                  <div>
                    <h6 className='mb-0 text-muted small'>Selected Period</h6>
                    <p className='mb-0 fw-semibold text-dark'>{getDateRangeLabel()}</p>
                  </div>
                </div>
                {selectedFeatureName.name && (
                  <div className='ms-auto d-flex align-items-center'>
                    <div className='bg-success bg-opacity-10 rounded-circle p-2 me-3'>
                      <i className='fas fa-chart-line text-success'></i>
                    </div>
                    <div className='text-end'>
                      <h6 className='mb-0 text-muted small'>Selected Parameter</h6>
                      <p className='mb-0 fw-semibold text-dark'>
                        {selectedFeatureName.name}
                        {selectedFeatureName.unit && (
                          <span className='text-muted ms-1'>({selectedFeatureName.unit})</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Component */}
      <BenchmarkFilter
        companyFilter={companyFilter}
        setCompanyFilter={setCompanyFilter}
        siteFilter={siteFilter}
        setSiteFilter={setSiteFilter}
        setMeterType={setMeterType}
        meterType={meterType}
        period={period}
        setPeriod={setPeriod}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedFeature={selectedFeature}
        setSelectedFeature={setSelectedFeature}
        setSelectedFeatureName={setSelectedFeatureName}
      />
      {/* Quick Decision Heatmaps - Only show for 10 or fewer sites */}
      {chartData && data && data.sites.length <= 10 && (
        <div className='row mt-4'>
          <div className='col-12'>
            <div className='card border-0 shadow-sm'>
              <div className='card-header bg-white border-bottom'>
                <div className='d-flex align-items-center'>
                  <div>
                    <h5 className='mb-1 text-dark'>
                      <i className='fas fa-th text-primary me-2'></i>
                      Quick Decision Heatmaps
                    </h5>
                    <p className='mb-0 text-muted small'>
                      Visual comparison across all metrics for rapid site assessment (
                      {data.sites.length} sites)
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body'>
                <div className='row g-4'>
                  {/* Electricity Heatmap */}
                  <div className='col-lg-6 col-xl-6'>
                    <div className='border rounded p-3 h-100'>
                      <h6 className='text-primary mb-3 text-center fw-semibold'>
                        <i className='fas fa-bolt me-2'></i>
                        Electricity Consumption
                      </h6>
                      <ApexCharts
                        options={{
                          ...getHeatmapOptions('', 'electricity'),
                          chart: {
                            ...getHeatmapOptions('', 'electricity').chart,
                            height: Math.max(300, data.sites.length * 40), // Daha büyük hücreler
                          },
                          dataLabels: {
                            enabled: true,
                            style: {
                              colors: ['#fff'],
                              fontSize: '12px', // Daha büyük font
                              fontWeight: 600,
                            },
                            formatter: function (val: number) {
                              return formatValue(val, 'kWh')
                            },
                          },
                          yaxis: {
                            labels: {
                              style: {
                                colors: '#64748B',
                                fontSize: '12px', // Daha büyük font
                                fontWeight: 500,
                              },
                            },
                          },
                          plotOptions: {
                            heatmap: {
                              radius: 4, // Daha büyük radius
                              enableShades: true,
                              shadeIntensity: 0.5,
                              colorScale: {
                                ranges: [
                                  {
                                    from: 0,
                                    to: Math.max(...data.electricity) * 0.3,
                                    color: '#e3f2fd',
                                    name: 'Low',
                                  },
                                  {
                                    from: Math.max(...data.electricity) * 0.3,
                                    to: Math.max(...data.electricity) * 0.7,
                                    color: '#2196f3',
                                    name: 'Medium',
                                  },
                                  {
                                    from: Math.max(...data.electricity) * 0.7,
                                    to: Math.max(...data.electricity),
                                    color: '#0d47a1',
                                    name: 'High',
                                  },
                                ],
                              },
                            },
                          },
                        }}
                        series={data.sites.map((site, index) => ({
                          name: site,
                          data: [{ x: 'Electricity', y: data.electricity[index] }],
                        }))}
                        type='heatmap'
                        height={Math.max(300, data.sites.length * 40)}
                      />
                      <div className='mt-2 text-center'>
                        <small className='text-muted d-block'>
                          <strong>Range:</strong>{' '}
                          {formatValue(Math.min(...data.electricity), 'kWh')} -{' '}
                          {formatValue(Math.max(...data.electricity), 'kWh')}
                        </small>
                        <small className='text-muted'>
                          <strong>Avg:</strong>{' '}
                          {formatValue(
                            data.electricity.reduce((a, b) => a + b, 0) / data.electricity.length,
                            'kWh'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Gas Heatmap */}
                  <div className='col-lg-6 col-xl-6'>
                    <div className='border rounded p-3 h-100'>
                      <h6 className='text-success mb-3 text-center fw-semibold'>
                        <i className='fas fa-fire me-2'></i>
                        Gas Consumption
                      </h6>
                      <ApexCharts
                        options={{
                          ...getHeatmapOptions('', 'gas'),
                          chart: {
                            ...getHeatmapOptions('', 'gas').chart,
                            height: Math.max(300, data.sites.length * 40),
                          },
                          dataLabels: {
                            enabled: true,
                            style: {
                              colors: ['#fff'],
                              fontSize: '12px',
                              fontWeight: 600,
                            },
                            formatter: function (val: number) {
                              return formatValue(val, 'kWh')
                            },
                          },
                          yaxis: {
                            labels: {
                              style: {
                                colors: '#64748B',
                                fontSize: '12px',
                                fontWeight: 500,
                              },
                            },
                          },
                          plotOptions: {
                            heatmap: {
                              radius: 4,
                              enableShades: true,
                              shadeIntensity: 0.5,
                              colorScale: {
                                ranges: [
                                  {
                                    from: 0,
                                    to: Math.max(...data.gas) * 0.3,
                                    color: '#e8f5e8',
                                    name: 'Low',
                                  },
                                  {
                                    from: Math.max(...data.gas) * 0.3,
                                    to: Math.max(...data.gas) * 0.7,
                                    color: '#4caf50',
                                    name: 'Medium',
                                  },
                                  {
                                    from: Math.max(...data.gas) * 0.7,
                                    to: Math.max(...data.gas),
                                    color: '#1b5e20',
                                    name: 'High',
                                  },
                                ],
                              },
                            },
                          },
                        }}
                        series={data.sites.map((site, index) => ({
                          name: site,
                          data: [{ x: 'Gas', y: data.gas[index] }],
                        }))}
                        type='heatmap'
                        height={Math.max(300, data.sites.length * 40)}
                      />
                      <div className='mt-2 text-center'>
                        <small className='text-muted d-block'>
                          <strong>Range:</strong> {formatValue(Math.min(...data.gas), 'kWh')} -{' '}
                          {formatValue(Math.max(...data.gas), 'kWh')}
                        </small>
                        <small className='text-muted'>
                          <strong>Avg:</strong>{' '}
                          {formatValue(
                            data.gas.reduce((a, b) => a + b, 0) / data.gas.length,
                            'kWh'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row g-4 mt-2'>
                  {/* Total Heatmap */}
                  <div className='col-lg-6 col-xl-6'>
                    <div className='border rounded p-3 h-100'>
                      <h6 className='text-warning mb-3 text-center fw-semibold'>
                        <i className='fas fa-chart-bar me-2'></i>
                        Total Consumption
                      </h6>
                      <ApexCharts
                        options={{
                          ...getHeatmapOptions('', 'total'),
                          chart: {
                            ...getHeatmapOptions('', 'total').chart,
                            height: Math.max(300, data.sites.length * 40),
                          },
                          dataLabels: {
                            enabled: true,
                            style: {
                              colors: ['#fff'],
                              fontSize: '12px',
                              fontWeight: 600,
                            },
                            formatter: function (val: number) {
                              return formatValue(val, 'kWh')
                            },
                          },
                          yaxis: {
                            labels: {
                              style: {
                                colors: '#64748B',
                                fontSize: '12px',
                                fontWeight: 500,
                              },
                            },
                          },
                          plotOptions: {
                            heatmap: {
                              radius: 4,
                              enableShades: true,
                              shadeIntensity: 0.5,
                              colorScale: {
                                ranges: [
                                  {
                                    from: 0,
                                    to: Math.max(...data.total) * 0.3,
                                    color: '#fff3cd',
                                    name: 'Low',
                                  },
                                  {
                                    from: Math.max(...data.total) * 0.3,
                                    to: Math.max(...data.total) * 0.7,
                                    color: '#ffc107',
                                    name: 'Medium',
                                  },
                                  {
                                    from: Math.max(...data.total) * 0.7,
                                    to: Math.max(...data.total),
                                    color: '#856404',
                                    name: 'High',
                                  },
                                ],
                              },
                            },
                          },
                        }}
                        series={data.sites.map((site, index) => ({
                          name: site,
                          data: [{ x: 'Total', y: data.total[index] }],
                        }))}
                        type='heatmap'
                        height={Math.max(300, data.sites.length * 40)}
                      />
                      <div className='mt-2 text-center'>
                        <small className='text-muted d-block'>
                          <strong>Range:</strong> {formatValue(Math.min(...data.total), 'kWh')} -{' '}
                          {formatValue(Math.max(...data.total), 'kWh')}
                        </small>
                        <small className='text-muted'>
                          <strong>Avg:</strong>{' '}
                          {formatValue(
                            data.total.reduce((a, b) => a + b, 0) / data.total.length,
                            'kWh'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Per Feature Heatmap */}
                  <div className='col-lg-6 col-xl-6'>
                    <div className='border rounded p-3 h-100'>
                      <h6 className='text-info mb-3 text-center fw-semibold'>
                        <i className='fas fa-chart-pie me-2'></i>
                        Per {selectedFeatureName.name || 'Feature'}
                      </h6>
                      <ApexCharts
                        options={{
                          ...getHeatmapOptions('', 'perFeature'),
                          chart: {
                            ...getHeatmapOptions('', 'perFeature').chart,
                            height: Math.max(300, data.sites.length * 40),
                          },
                          dataLabels: {
                            enabled: true,
                            style: {
                              colors: ['#fff'],
                              fontSize: '12px',
                              fontWeight: 600,
                            },
                            formatter: function (val: number) {
                              return formatValue(val, selectedFeatureName.unit)
                            },
                          },
                          yaxis: {
                            labels: {
                              style: {
                                colors: '#64748B',
                                fontSize: '12px',
                                fontWeight: 500,
                              },
                            },
                          },
                          plotOptions: {
                            heatmap: {
                              radius: 4,
                              enableShades: true,
                              shadeIntensity: 0.5,
                              colorScale: {
                                ranges: [
                                  {
                                    from: 0,
                                    to: Math.max(...data.perFeature) * 0.3,
                                    color: '#e0f7fa',
                                    name: 'Low',
                                  },
                                  {
                                    from: Math.max(...data.perFeature) * 0.3,
                                    to: Math.max(...data.perFeature) * 0.7,
                                    color: '#00bcd4',
                                    name: 'Medium',
                                  },
                                  {
                                    from: Math.max(...data.perFeature) * 0.7,
                                    to: Math.max(...data.perFeature),
                                    color: '#006064',
                                    name: 'High',
                                  },
                                ],
                              },
                            },
                          },
                        }}
                        series={data.sites.map((site, index) => ({
                          name: site,
                          data: [
                            { x: selectedFeatureName.unit || 'Unit', y: data.perFeature[index] },
                          ],
                        }))}
                        type='heatmap'
                        height={Math.max(300, data.sites.length * 40)}
                      />
                      <div className='mt-2 text-center'>
                        <small className='text-muted d-block'>
                          <strong>Range:</strong>{' '}
                          {formatValue(Math.min(...data.perFeature), selectedFeatureName.unit)} -{' '}
                          {formatValue(Math.max(...data.perFeature), selectedFeatureName.unit)}
                        </small>
                        <small className='text-muted'>
                          <strong>Avg:</strong>{' '}
                          {formatValue(
                            data.perFeature.reduce((a, b) => a + b, 0) / data.perFeature.length,
                            selectedFeatureName.unit
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className='row mt-3'>
                  <div className='col-12'>
                    <div className='bg-light rounded p-3'>
                      <h6 className='mb-2 text-muted'>
                        <i className='fas fa-info-circle me-2'></i>
                        Color Scale Legend
                      </h6>
                      <div className='row text-center'>
                        <div className='col-3'>
                          <div className='d-flex align-items-center justify-content-center'>
                            <div
                              className='bg-light border rounded px-2 py-1 me-2'
                              style={{ backgroundColor: '#e3f2fd' }}
                            ></div>
                            <small>Low (0-30%)</small>
                          </div>
                        </div>
                        <div className='col-3'>
                          <div className='d-flex align-items-center justify-content-center'>
                            <div className='bg-primary rounded px-2 py-1 me-2'></div>
                            <small>Medium (30-70%)</small>
                          </div>
                        </div>
                        <div className='col-3'>
                          <div className='d-flex align-items-center justify-content-center'>
                            <div
                              className='rounded px-2 py-1 me-2'
                              style={{ backgroundColor: '#0d47a1' }}
                            ></div>
                            <small>High (70-100%)</small>
                          </div>
                        </div>
                        <div className='col-3'>
                          <small className='text-muted'>
                            Values are relative to maximum in each category
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info message for many sites */}
      {chartData && data && data.sites.length > 10 && (
        <div className='row mt-4'>
          <div className='col-12'>
            <div className='alert alert-info border-0 shadow-sm'>
              <div className='d-flex align-items-center'>
                <i className='fas fa-info-circle text-info me-3 fs-4'></i>
                <div>
                  <h6 className='mb-1 text-info'>
                    Many Sites Detected ({data.sites.length} sites)
                  </h6>
                  <p className='mb-0 small'>
                    Quick Decision Heatmaps are hidden for better readability with many sites. Use
                    the detailed charts below for comprehensive analysis and comparison.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Energy Consumption Analysis */}
      <div className='row mt-4'>
        <div className='col-12'>
          <div className='card border-0 shadow-sm'>
            <div className='card-header bg-white border-bottom'>
              <div className='d-flex align-items-center justify-content-between'>
                <div>
                  <h5 className='mb-1 text-dark'>
                    <i className='fas fa-chart-line text-primary me-2'></i>
                    Energy Consumption Analysis
                  </h5>
                  <p className='mb-0 text-muted small'>
                    Complete energy breakdown with combination chart visualization
                  </p>
                </div>
                {isLoading && (
                  <div className='d-flex align-items-center text-muted'>
                    <div className='spinner-border spinner-border-sm me-2' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <span className='small'>Loading data...</span>
                  </div>
                )}
              </div>
            </div>
            <div className='card-body'>
              {chartData && data ? (
                <div className='position-relative'>
                  <ApexCharts
                    options={getCombinationOptions()}
                    series={getCombinationSeries()}
                    type='line'
                    height={Math.min(500, Math.max(400, data.sites.length * 8))}
                  />
                </div>
              ) : !isLoading ? (
                <div className='text-center py-5'>
                  <div className='mb-3'>
                    <i className='fas fa-chart-line text-muted' style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className='text-muted mb-2'>No Data Available</h5>
                  <p className='text-muted mb-0'>
                    Please select a company and parameter to view the analysis.
                  </p>
                </div>
              ) : (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary mb-3' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  <h5 className='text-muted'>Loading Chart Data...</h5>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Radar Analysis */}
      {chartData && data && (
        <div className='row mt-4'>
          <div className='col-12'>
            <div className='card border-0 shadow-sm'>
              <div className='card-header bg-white border-bottom'>
                <div className='d-flex align-items-center'>
                  <div>
                    <h5 className='mb-1 text-dark'>
                      <i className='fas fa-chart-pie text-primary me-2'></i>
                      Radar Analysis
                    </h5>
                    <p className='mb-0 text-muted small'>
                      Multi-dimensional comparison across all sites with average benchmarks
                    </p>
                  </div>
                </div>
              </div>
              <div className='card-body'>
                <div className='row g-4'>
                  {/* Electricity Radar */}
                  <div className='col-lg-6'>
                    <div className='bg-light bg-opacity-50 rounded p-3 h-100 text-center'>
                      <h6 className='text-primary mb-2 fw-semibold'>
                        <i className='fas fa-bolt me-2'></i>
                        Electricity Consumption
                      </h6>
                      <div style={{ paddingTop: '10px' }}>
                        <ApexCharts
                          options={getRadarOptions('#007bff')}
                          series={getElectricityRadarSeries()}
                          type='radar'
                          height={Math.min(350, Math.max(250, data.sites.length * 4))}
                        />
                      </div>
                      <div className='mt-1'>
                        <small className='text-muted'>
                          <strong>Average:</strong>{' '}
                          {formatValue(
                            data.electricity.reduce((a, b) => a + b, 0) / data.electricity.length,
                            'kWh'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Gas Radar */}
                  <div className='col-lg-6'>
                    <div className='bg-light bg-opacity-50 rounded p-3 h-100 text-center'>
                      <h6 className='text-success mb-2 fw-semibold'>
                        <i className='fas fa-fire me-2'></i>
                        Gas Consumption
                      </h6>
                      <div style={{ paddingTop: '10px' }}>
                        <ApexCharts
                          options={getRadarOptions('#28a745')}
                          series={getGasRadarSeries()}
                          type='radar'
                          height={Math.min(350, Math.max(250, data.sites.length * 4))}
                        />
                      </div>
                      <div className='mt-1'>
                        <small className='text-muted'>
                          <strong>Average:</strong>{' '}
                          {formatValue(
                            data.gas.reduce((a, b) => a + b, 0) / data.gas.length,
                            'kWh'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Total Consumption Radar */}
                  <div className='col-lg-6'>
                    <div className='bg-light bg-opacity-50 rounded p-3 h-100 text-center'>
                      <h6 className='text-warning mb-2 fw-semibold'>
                        <i className='fas fa-chart-bar me-2'></i>
                        Total Consumption
                      </h6>
                      <div style={{ paddingTop: '10px' }}>
                        <ApexCharts
                          options={getRadarOptions('#ffc107')}
                          series={getTotalRadarSeries()}
                          type='radar'
                          height={Math.min(350, Math.max(250, data.sites.length * 4))}
                        />
                      </div>
                      <div className='mt-1'>
                        <small className='text-muted'>
                          <strong>Average:</strong>{' '}
                          {formatValue(
                            data.total.reduce((a, b) => a + b, 0) / data.total.length,
                            'kWh'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Per Feature Radar */}
                  <div className='col-lg-6'>
                    <div className='bg-light bg-opacity-50 rounded p-3 h-100 text-center'>
                      <h6 className='text-info mb-2 fw-semibold'>
                        <i className='fas fa-chart-pie me-2'></i>
                        Per {selectedFeatureName.name || 'Feature'}
                      </h6>
                      <div style={{ paddingTop: '10px' }}>
                        <ApexCharts
                          options={getRadarOptions('#17a2b8')}
                          series={getPerFeatureRadarSeries()}
                          type='radar'
                          height={Math.min(350, Math.max(250, data.sites.length * 4))}
                        />
                      </div>
                      <div className='mt-1'>
                        <small className='text-muted'>
                          <strong>Average:</strong>{' '}
                          {formatValue(
                            data.perFeature.reduce((a, b) => a + b, 0) / data.perFeature.length,
                            selectedFeatureName.unit
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. Key Statistics */}
      {chartData && data && (
        <div className='row mt-4'>
          <div className='col-12'>
            <div className='card border-0 shadow-sm'>
              <div className='card-body'>
                <h6 className='text-muted mb-3'>
                  <i className='fas fa-chart-bar me-2'></i>
                  Key Statistics
                </h6>
                <div className='row g-3'>
                  {/* Energy Mix */}
                  <div className='col-md-4'>
                    <div className='bg-primary bg-opacity-10 rounded p-3 border border-primary border-opacity-25'>
                      <h6 className='text-primary mb-2'>
                        <i className='fas fa-bolt me-2'></i>
                        Average Energy Mix
                      </h6>
                      <div className='small'>
                        <div className='d-flex justify-content-between mb-1'>
                          <span>Electricity:</span>
                          <strong>
                            {formatValue(
                              data.electricity.reduce((a, b) => a + b, 0) / data.electricity.length,
                              'kWh'
                            )}
                          </strong>
                        </div>
                        <div className='d-flex justify-content-between mb-1'>
                          <span>Gas:</span>
                          <strong>
                            {formatValue(
                              data.gas.reduce((a, b) => a + b, 0) / data.gas.length,
                              'kWh'
                            )}
                          </strong>
                        </div>
                        <div className='d-flex justify-content-between'>
                          <span className='text-warning'>Total:</span>
                          <strong className='text-warning'>
                            {formatValue(
                              data.total.reduce((a, b) => a + b, 0) / data.total.length,
                              'kWh'
                            )}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lowest Per Feature */}
                  <div className='col-md-4'>
                    <div className='bg-success bg-opacity-10 rounded p-3 border border-success border-opacity-25'>
                      <h6 className='text-success mb-2'>
                        <i className='fas fa-arrow-down me-2'></i>
                        Lowest Per {selectedFeatureName.name}
                      </h6>
                      <p className='mb-1 fw-semibold'>
                        {data.sites[data.perFeature.indexOf(Math.min(...data.perFeature))]}
                      </p>
                      <small className='text-muted'>
                        {formatValue(Math.min(...data.perFeature), selectedFeatureName.unit)} per{' '}
                        {selectedFeatureName.name}
                      </small>
                    </div>
                  </div>

                  {/* Highest Consumer */}
                  <div className='col-md-4'>
                    <div className='bg-warning bg-opacity-10 rounded p-3 border border-warning border-opacity-25'>
                      <h6 className='text-warning mb-2'>
                        <i className='fas fa-arrow-up me-2'></i>
                        Highest Total Consumption
                      </h6>
                      <p className='mb-1 fw-semibold'>
                        {data.sites[data.total.indexOf(Math.max(...data.total))]}
                      </p>
                      <small className='text-muted'>
                        {formatValue(Math.max(...data.total), 'kWh')} total consumption
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .apexcharts-toolbar {
          background: white !important;
          border-radius: 6px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        }
        
        .card-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
        }
        
        .apexcharts-radar-series {
          transition: all 0.3s ease;
        }
        
        .apexcharts-radar-series:hover {
          filter: brightness(1.1);
        }
        
        .apexcharts-xaxis-label {
          font-size: 9px !important;
          fill: #64748B !important;
        }
        
        .apexcharts-datalabel {
          font-size: 10px !important;
        }
        
        .apexcharts-heatmap-rect {
          transition: all 0.3s ease;
        }
        
        .apexcharts-heatmap-rect:hover {
          transform: scale(1.05);
          filter: brightness(1.1);
        }
        
        @media (max-width: 768px) {
          .apexcharts-xaxis-label {
            font-size: 8px !important;
          }
        }
      `}</style>
    </>
  )
}

export default BenchmarkHeatmap
